"use client";

import { useState, useTransition } from "react";
import { Globe } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { setPublished as setStoryPublished, setStoryVisibility } from "@/lib/actions/story-actions";
interface PublishStoryPromptDialogProps {
  storyId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Called after the story has been successfully made public, so the editor
   * stops re-prompting when the author publishes further chapters.
   */
  onStoryPublished: () => void;
}

/**
 * Shown right after an author publishes a chapter while the story itself isn't
 * publicly visible yet. Explains that the chapter won't reach readers until the
 * story is public, and offers a one-click "make it public" action (sets both
 * the `published` and `visibility` flags the browse query requires).
 */
export function PublishStoryPromptDialog({
  storyId,
  open,
  onOpenChange,
  onStoryPublished
}: PublishStoryPromptDialogProps) {
  const [isPublishing, startPublishing] = useTransition();
  const [error, setError] = useState<string | null>(null);
  function handleMakePublic() {
    setError(null);
    startPublishing(async () => {
      try {
        const published = await setStoryPublished(storyId, true);
        const visible = await setStoryVisibility(storyId, "public");
        if (!published?.success || !visible?.success) {
          setError("Something went wrong. Please try again.");
          return;
        }
        onStoryPublished();
        onOpenChange(false);
      } catch {
        setError("Something went wrong. Please try again.");
      }
    });
  }
  return <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <div className="mx-auto mb-2 flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary sm:mx-0">
                        <Globe className="size-6" />
                    </div>
                    <DialogTitle>
                        Chapter published — but the story isn’t public yet
                    </DialogTitle>
                    <DialogDescription>
                        Publishing a chapter only reaches readers once the story
                        itself is public. Right now this story won’t appear in
                        browse, search, or for readers. Make it public to let
                        people find and read it.
                    </DialogDescription>
                </DialogHeader>

                {error && <p className="text-sm text-destructive" role="alert">
                        {error}
                    </p>}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPublishing}>
                        Not now
                    </Button>
                    <Button onClick={handleMakePublic} disabled={isPublishing}>
                        {isPublishing ? "Publishing…" : "Make story public"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>;
}